<template>
  <transition name="modal">
    <div class="modal-mask">
      <div class="modal-wrapper">
        <div class="modal-container import-csv-modal">
          <div class="modal-header">
            <i class="close icon" v-on:click="$emit('close')"></i>
            <h3>Import Price List .csv</h3>
          </div>

          <div class="ui divider"></div>

          <div class="modal-body scrolling content">
            <form class="ui form">
              <div class="field settings">
                <label>Supplier</label>
                <app-select
                  :defaultLabel="'Select supplier'"
                  :value="supplier"
                  :items="$store.getters['product/getSuppliers']"
                  @selected="onSupplierSelected"
                />
                <p v-if="supplier !== 1" class="import-help">
                  Only the Reece CSV adapter is available currently.
                </p>
              </div>

              <div class="field settings">
                <label class="small">File Upload</label>
                <div class="ui divider"></div>
                <div class="fields">
                  <div class="field">
                    <label id="csv-upload-label" for="csv-upload" class="ui basic button">Upload</label>
                    <input type="file" name="file" id="csv-upload" accept=".csv,text/csv" v-on:change="onFileChange">
                    <span v-if="fileName" style="margin-left: 0.75rem;">{{ fileName }}</span>
                    <span v-else style="margin-left: 0.75rem;">Format needs to be in .csv format</span>
                  </div>
                </div>
              </div>

              <div v-if="preview" class="import-preview">
                <h4 class="ui dividing header">Import preview</h4>
                <div class="ui list">
                  <div class="item"><strong>Products:</strong> {{ preview.total }}</div>
                  <div class="item"><strong>Mapped headings:</strong> {{ preview.mapped_headings }}</div>
                  <div class="item">
                    <strong>Needs review:</strong>
                    <span :class="{ 'text-warn': preview.unmapped_headings > 0 }">
                      {{ preview.unmapped_headings }}
                    </span>
                  </div>
                </div>

                <div v-if="unmappedHeadings.length" class="ui warning message">
                  <div class="header">Headings needing classification</div>
                  <ul class="list">
                    <li v-for="h in unmappedHeadings.slice(0, 12)" :key="h.source_heading_normalized">
                      {{ h.source_heading }} ({{ h.product_count }})
                    </li>
                  </ul>
                  <p v-if="unmappedHeadings.length > 12">
                    …and {{ unmappedHeadings.length - 12 }} more. Resolve via taxonomy mappings after import, or map before confirming.
                  </p>
                </div>

                <div v-if="preview.sample && preview.sample.length" class="sample-rows">
                  <h5>Sample rows</h5>
                  <table class="ui celled compact table">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Description</th>
                        <th>Cost</th>
                        <th>Sell (markup)</th>
                        <th>Group</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in preview.sample" :key="row.supplier_id">
                        <td>{{ row.supplier_id }}</td>
                        <td>{{ row.description }}</td>
                        <td>${{ formatMoney(row.cost) }}</td>
                        <td>${{ formatMoney(row.sell_price) }}</td>
                        <td>{{ row.family || 'needs review' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </form>
          </div>

          <div class="modal-footer">
            <div class="ui basic button smardie-btn-secondary" v-on:click="$emit('close')" :class="{ disabled: loading }">Cancel</div>
            <div
              class="ui basic button"
              v-on:click="runPreview"
              :class="{ loading: previewing, disabled: loading || previewing || !selectedFile || supplier !== 1 }"
            >
              Preview
            </div>
            <div class="ui right primary button"
                 v-on:click="upload"
                 :class="{ loading: loading, disabled: loading || previewing || !selectedFile || supplier !== 1 }">
              {{ preview ? 'Confirm import' : 'Upload' }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from 'axios'
import Select from '../../common/select/select.vue'

export default {
  name: 'ImportCsvModal',
  components: {
    'app-select': Select
  },
  data () {
    return {
      supplier: 1,
      selectedFile: null,
      fileName: '',
      loading: false,
      previewing: false,
      preview: null
    }
  },
  computed: {
    unmappedHeadings () {
      if (!this.preview || !Array.isArray(this.preview.headings)) return []
      return this.preview.headings.filter(h => h.needs_review)
    }
  },
  created () {
    this.$store.dispatch('product/fetchSuppliers')
  },
  methods: {
    formatMoney (value) {
      const n = parseFloat(value)
      return Number.isFinite(n) ? n.toFixed(2) : '0.00'
    },
    onSupplierSelected (event) {
      this.supplier = event.id
      this.preview = null
    },
    onFileChange (event) {
      const files = event.target.files
      if (files && files.length > 0) {
        this.selectedFile = files[0]
        this.fileName = files[0].name
      } else {
        this.selectedFile = null
        this.fileName = ''
      }
      this.preview = null
    },
    buildFormData () {
      const formData = new FormData()
      formData.append('file', this.selectedFile)
      formData.append('supplier', this.supplier)
      formData.append('mode', 'full')
      return formData
    },
    runPreview () {
      if (!this.selectedFile || this.loading || this.previewing) return
      if (this.supplier !== 1) {
        alert('Only the Reece CSV adapter is available currently.')
        return
      }
      this.previewing = true
      axios.post('/api/materials/import/csv/preview', this.buildFormData()).then(response => {
        this.preview = (response.data && response.data.preview) || null
      }).catch(error => {
        const msg = (error.response && error.response.data && error.response.data.error) || error.message || 'Preview failed'
        alert('Preview failed: ' + msg)
      }).finally(() => {
        this.previewing = false
      })
    },
    upload () {
      if (!this.selectedFile || this.loading) return
      if (this.supplier !== 1) {
        alert('Only the Reece CSV adapter is available currently.')
        return
      }

      this.loading = true
      axios.post('/api/materials/import/csv', this.buildFormData()).then(response => {
        const msg = (response.data && response.data.message) || 'Import complete'
        this.successNotification(msg)
        this.$emit('imported', response.data && response.data.summary)
        this.$emit('close')
      }).catch(error => {
        const msg = (error.response && error.response.data && error.response.data.error) || error.message || 'Import failed'
        alert('Import failed: ' + msg)
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style scoped>
#import-csv,
#csv-upload {
  opacity: 0;
  position: absolute;
  z-index: -1;
}
.import-csv-modal {
  max-width: 720px;
}
.import-help,
.task-group-help {
  margin-top: 0.35rem;
  color: var(--color-mid-grey);
  font-size: 0.9rem;
}
.text-warn {
  color: var(--color-mid-grey);
  font-weight: 600;
}
.sample-rows {
  margin-top: 1rem;
  overflow-x: auto;
}
</style>
